<template>
  <div
    class="smooth-resizer"
    :class="{ 'smooth-resizer--active': isActiveInternal }"
    :style="wrapperStyles"
  >
    <div ref="inner" class="smooth-resizer__inner">
      <slot />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'SmoothResizer',

  props: {
    minWidth: {
      type: Number,
      default: 0,
    },

    minHeight: {
      type: Number,
      default: 0,
    },

    isActive: {
      type: Boolean,
      default: true,
    },

    isObservingWidth: {
      type: Boolean,
      default: false,
    },

    isObservingHeight: {
      type: Boolean,
      default: true,
    },

    transitionTime: {
      type: Number,
      default: On.TransitionTimes.medium,
    },
  },

  data() {
    return {
      height: 0,
      isResizeObserverSupported: !!window.ResizeObserver,
      observer: undefined,
      styles: {
        height: 0,
        width: 0,
      },
      timeouts: {
        height: null,
        width: null,
      },
      width: 0,
    }
  },

  computed: {
    ...mapGetters({
      viewportSize: 'viewport/dimensions',
    }),

    wrapperStyles() {
      const transitions = [
        `width ${this.transitionTime}ms ease-in-out`,
        `height ${this.transitionTime}ms ease-in-out`,
      ]
      const styles = { transition: transitions.join(', ') }

      if (this.isActiveInternal) {
        const { width, height } = this.styles
        styles.width = this.isObservingWidth && width ? `${width}px` : ''
        styles.height = this.isObservingHeight && height ? `${height}px` : ''
      }
      return styles
    },

    isActiveInternal() {
      return this.isActive && !!this.isResizeObserverSupported
    },
  },

  watch: {
    viewportSize: 'resetComputedSize',

    width() {
      if (this.isObservingWidth) this.onResize('width')
    },

    height() {
      if (this.isObservingHeight) this.onResize('height')
    },

    isActive() {
      this.isActive ? this.init() : this.kill()
    },
  },

  mounted() {
    this.init()
  },

  beforeDestroy() {
    this.kill()
  },

  methods: {
    init() {
      if (!this.isActiveInternal || this.observer || !this.$refs.inner) return

      this.resetComputedSize()
      this.observer = new ResizeObserver(entries => {
        entries.forEach(entry => {
          this.setWidth(Math.round(entry.contentRect.width))
          this.setHeight(Math.round(entry.contentRect.height))
        })
      })
      this.observer.observe(this.$refs.inner)
    },

    kill() {
      this.resetStyles()
      this.observer?.disconnect()
      this.observer = undefined
    },

    setWidth(width) {
      return (this.width = Math.max(width, this.minWidth))
    },

    setHeight(height) {
      return (this.height = Math.max(height, this.minHeight))
    },

    resetStyles() {
      this.styles.width = ''
      this.styles.height = ''
    },

    async resetComputedSize() {
      if (!this.isActiveInternal) return

      this.resetStyles()
      await this.$nextTick()
      this.styles.width = this.setWidth(this.$el.offsetWidth)
      this.styles.height = this.setHeight(this.$el.offsetHeight)
    },

    async onResize(sizeProperty) {
      if (!this.isActiveInternal || this.timeouts[sizeProperty]) return

      const addition = this[sizeProperty] - this.styles[sizeProperty]
      if (!addition) return

      this.$emit(`before-${sizeProperty}-change`, addition)
      this.styles[sizeProperty] = this[sizeProperty]
      this.timeouts[sizeProperty] = setTimeout(
        () => this.afterResize(sizeProperty),
        this.transitionTime
      )
    },

    afterResize(sizeProperty) {
      this.timeouts[sizeProperty] = null
      this.$emit(`after-${sizeProperty}-change`)
    },
  },
}
</script>

<style lang="scss" scoped>
.smooth-resizer {
  &--active {
    overflow: hidden;
    will-change: width, height;
  }
}
</style>
